import React, { Component, memo, Suspense, useLayoutEffect, useState } from 'react';
import { Button } from '@Sage/hoi-poi-ui';
import { useLocation } from 'react-router';

import AbsoluteEmpty from 'components/AbsoluteEmpty';
import AbsoluteLoader from 'components/AbsoluteLoader';
import { notifyBugsnagError } from 'BugSnagWrapper';
import { getLiteral } from 'utils/getLiteral';
import {
    getRouteResolutionVersion,
    isRouteChunkLoadError,
    subscribeToRouteResolution,
} from 'utils/lazyRoute';

const reloadPage = () => window.location.reload();

class RouteChunkErrorBoundary extends Component {
    state = { error: null, resetKey: null };

    static getDerivedStateFromProps(props, state) {
        if (props.resetKey !== state.resetKey) {
            return { error: null, resetKey: props.resetKey };
        }

        return null;
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error) {
        if (isRouteChunkLoadError(error)) {
            notifyBugsnagError(error.originalError || error);
        }
    }

    render() {
        const { children, onReload } = this.props;
        const { error } = this.state;

        if (!error) return children;
        if (!isRouteChunkLoadError(error)) throw error;

        return (
            <AbsoluteEmpty
                actions={[
                    <Button key="reload-route" onClick={onReload}>
                        {getLiteral('action_refresh')}
                    </Button>,
                ]}
            />
        );
    }
}

const LazyRouteContent = memo(({ children, onReload = reloadPage }) => {
    const location = useLocation();
    const locationKey = `${location.key}:${location.pathname}:${location.search}:${location.hash}`;
    const routeHash = `#${location.pathname}${location.search}${location.hash}`;
    const [resolutionVersion, setResolutionVersion] = useState(() =>
        getRouteResolutionVersion(routeHash),
    );

    useLayoutEffect(() => {
        const syncResolutionVersion = (resolvedHash, version) => {
            if (resolvedHash === routeHash) setResolutionVersion(version);
        };
        const unsubscribe = subscribeToRouteResolution(syncResolutionVersion);

        setResolutionVersion(getRouteResolutionVersion(routeHash));
        return unsubscribe;
    }, [routeHash]);

    return (
        <RouteChunkErrorBoundary
            onReload={onReload}
            resetKey={`${locationKey}:${resolutionVersion}`}
        >
            <Suspense fallback={<AbsoluteLoader size="large" />}>{children}</Suspense>
        </RouteChunkErrorBoundary>
    );
});

LazyRouteContent.displayName = 'LazyRouteContent';

export default LazyRouteContent;
